const STORAGE_HOST = process.env.REACT_APP_STORAGE_HOST;

export enum UPLOAD_TYPE {
  RESOURCES = 'resources',
  CLAIM_ATTACHMENT = 'claim_attachments',
  CLAIM_PAYMENT_PROOF = 'claim_payment_proofs',
  CLAIM_TYPES = 'claim_types',
  POLICY_FILES = 'policy_files',
  MESSAGE_FILES = 'message_files',
  EMPLOYEE_PROFILE = 'employee_profile',
  HISTORY_EXTRACT = 'history_extract',
  EMPLOYER_PROFILE = 'employer_profile',
  CURRENCY_FLAG = 'currency_flag',
  VISION_TEST_FILES = 'vision_test_files',
  CLAIM_EXTRACT_FILES = 'claim_extract_files',
  STREAMS = 'streams',
  HOSPITAL = 'hospitals',
  HOSPITAL_RATING = 'hospital_ratings',
  MERCHANT = 'merchants',
  STORE = 'stores',
  STORE_RATING = 'store_ratings',
  PRODUCT = 'products',
  ORDER = 'orders',
  ORDER_INVOICE = 'order_invoice_files',
  ORDER_PAYMENT_PROOF = 'order_payment_proof_files',
  LEAD_MESSAGE = 'lead_message',
  LEAD_BULK_TEMPLATE = 'lead_bulk_template',
  BID_SNAPSHOT_BENEFIT = 'bid_snapshot_benefit',
  BID_PROPOSAL = 'bid_proposal',
  FONTS = 'fonts',
  ECARDS = 'ecards',
}

export enum ACCEPTED_FILES {
  IMAGE = 'image/*',
  EXCEL = '.xlsx,.xls',
  PDF_AND_DOC = '.doc,.docx,.pdf',
  FILES = '.xlsx,.xls,image/*,.doc,.docx,.ppt,.pptx,.txt,.pdf',
  FONTS = '.woff2,.woff,.ttf,.otf,.eot',
}

export const EXT_IMG_FILE = ['png', 'jpg', 'jpeg', 'gif', 'bmp'];
export const EXT_IMG_VIDEO = ['.mp4', '.m4a', '.f4v', '.m4b', 'mov'];
export const VALID_IMG_FILE = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/bmp'];
export const EXT_DOC_FILE = ['xlsx', 'xls', 'doc', 'docx', 'ppt', 'pptx', 'txt', 'pdf'];

/**
 * Upload file to storage with folder
 * @param file
 * @param folder: folder file
 * @param onProgressCallback: handle progressing while uploading file
 * @param onSuccessCallback: handle success after uploaded file
 * @param onErrorCallback: handle error after uploaded file
 */
const UploadFile = (
  file: File,
  folder: string,
  metadata: object | null,
  onProgressCallback?: Function,
  onSuccessCallback?: Function,
  onErrorCallback?: Function,
) => {
  if (!file) {
    return;
  }
  const auth = `Bearer ${localStorage.getItem('access-token')}`;
  const defaultRole = JSON.parse(localStorage.getItem('default-role') || '');
  let xUserRole: string | null = null;
  if (defaultRole) {
    xUserRole = defaultRole.Role;
  }
  const chunkSize = 5 * 1024 * 1024; // 5MB (minimum part size defined by aws s3)
  if (file.size <= chunkSize) {
    const formData = new FormData();
    formData.append('File', file);
    formData.append('Folder', folder);
    if (metadata && !isObjectEmpty(metadata)) {
      formData.append('Metadata', JSON.stringify(metadata));
    } else {
      formData.append('Metadata', '{}');
    }
    const requestHeaders: HeadersInit = new Headers();
    requestHeaders.set('Authorization', auth);
    if (xUserRole) {
      requestHeaders.set('X-User-Role', xUserRole);
    }
    fetch(`${STORAGE_HOST}/file/upload`, {
      method: 'POST',
      body: formData,
      headers: requestHeaders,
    })
      .then((response) => response.json())
      .then((data) => {
        if (onSuccessCallback) onSuccessCallback(data.Data);
      })
      .catch((error) => {
        console.log('Error uploading:', error);
        if (onErrorCallback) onErrorCallback(error);
      });
  } else {
    const totalChunks = Math.ceil(file.size / chunkSize);
    let chunkNumber = 0;
    let start = 0;
    let end = chunkSize;
    let result = {
      FileName: null,
      FilePath: null,
      MediaLink: null,
      Status: null,
      UploadId: null,
      PrevETags: null,
    };

    const uploadNextChunk = () => {
      if (chunkNumber < totalChunks) {
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append('File', chunk);
        formData.append('Folder', folder);
        if (metadata && !isObjectEmpty(metadata)) {
          formData.append('Metadata', JSON.stringify(metadata));
        } else {
          formData.append('Metadata', '{}');
        }
        formData.append(
          'Chunkdata',
          JSON.stringify({
            OriginFileName: file.name,
            ContentType: file.type,
            KeyName: result.FilePath,
            UploadId: result.UploadId,
            ChunkIndex: chunkNumber,
            ChunkMax: totalChunks,
            PrevETags: result.PrevETags,
          }),
        );
        const requestHeaders: HeadersInit = new Headers();
        requestHeaders.set('Authorization', auth);
        if (xUserRole) {
          requestHeaders.set('X-User-Role', xUserRole);
        }
        fetch(`${STORAGE_HOST}/file/upload-chunk`, {
          method: 'POST',
          body: formData,
          headers: requestHeaders,
        })
          .then((response) => response.json())
          .then((data) => {
            result = data.Data;
            chunkNumber += 1;
            start = end;
            // Check is last part
            if (chunkNumber + 1 === totalChunks) {
              end = file.size;
            } else {
              end = start + chunk.size;
            }
            if (onProgressCallback) onProgressCallback(chunkNumber, totalChunks);
            uploadNextChunk();
          })
          .catch((error) => {
            console.log('Error uploading chunk:', error);
            if (onErrorCallback) onErrorCallback(error);
          });
      } else {
        if (onProgressCallback) onProgressCallback(chunkNumber, totalChunks);
        if (onSuccessCallback) onSuccessCallback(result);
      }
    };
    uploadNextChunk();
  }
};

/**
 * Delete file from storage
 * @param path
 * @param onSuccessCallback: handle success after deleted file
 * @param onErrorCallback: handle error after deleted file
 */
const DeleteFile = (path: string, onSuccessCallback?: Function, onErrorCallback?: Function) => {
  if (!path) {
    return;
  }
  const auth = `Bearer ${localStorage.getItem('access-token')}`;
  const defaultRole = JSON.parse(localStorage.getItem('default-role') || '');
  let xUserRole: string | null = null;
  if (defaultRole) {
    xUserRole = defaultRole.Role;
  }
  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Authorization', auth);
  if (xUserRole) {
    requestHeaders.set('X-User-Role', xUserRole);
  }
  fetch(`${STORAGE_HOST}/file/${path}`, {
    method: 'DELETE',
    headers: requestHeaders,
  })
    .then((response) => response.json())
    .then(() => {
      if (onSuccessCallback) onSuccessCallback();
    })
    .catch((error) => {
      console.log('Error deleting:', error);
      if (onErrorCallback) onErrorCallback(error);
    });
};

/**
 * Get metadata file from storage
 * @param path
 * @param onSuccessCallback: handle success after get metadata file
 * @param onErrorCallback: handle error after get metadata file
 */
const GetMetadataFile = (path: string, onSuccessCallback?: Function, onErrorCallback?: Function) => {
  if (!path) {
    return;
  }
  const auth = `Bearer ${localStorage.getItem('access-token')}`;
  const defaultRole = JSON.parse(localStorage.getItem('default-role') || '');
  let xUserRole: string | null = null;
  if (defaultRole) {
    xUserRole = defaultRole.Role;
  }
  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Authorization', auth);
  if (xUserRole) {
    requestHeaders.set('X-User-Role', xUserRole);
  }
  fetch(`${STORAGE_HOST}/file/metadata/${path}`, {
    method: 'GET',
    headers: requestHeaders,
  })
    .then((response) => response.json())
    .then((data) => {
      if (onSuccessCallback) onSuccessCallback(data.Data);
    })
    .catch((error) => {
      console.log('Error get metadata:', error);
      if (onErrorCallback) onErrorCallback(error);
    });
};

const isObjectEmpty = (objectName: object | null) => {
  return objectName == null || Object.keys(objectName).length === 0;
};

export { UploadFile, DeleteFile, GetMetadataFile };